import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Typography,
} from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type {
  NotificationsResponse,
  SettingRange,
  SpeakerSettingsResponse,
  SpeakerSettingsValues,
} from "../api/types";
import { CountrySelect } from "../components/CountrySelect";

/** Turns a range from the server into the options of a bounded select. */
function optionsOf(range: SettingRange | undefined) {
  if (!range) return [];

  const options = [];
  for (let value = range.min; value <= range.max; value += range.step ?? 1) {
    options.push({ value, label: String(value) });
  }
  return options;
}

/**
 * Sidebar entry "speaker_settings" → /speaker/speaker_settings.
 *
 * How the speaker wants to be scheduled and reached: how far they travel, how
 * much room they want between two assignments, how far ahead they want to know,
 * whether they accept hospitality, and how they want to be reminded.
 *
 * Every bound comes from the server — three from the site configuration, one
 * from the circumscription, and the assignation range depends on the speaker's
 * own simultaneous_absence_limit, which the local coordinator sets. Hard-coding
 * any of them here would put the same rule in two places.
 *
 * The address is what the range check measures from, which is why the postcode
 * and country sit on this screen rather than only in the personal settings.
 */
export function SpeakerSettingsPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SpeakerSettingsValues>();

  const settings = useQuery({
    queryKey: ["speaker", "settings"],
    queryFn: () => apiFetch<SpeakerSettingsResponse>("/speaker/settings"),
  });

  useEffect(() => {
    if (!settings.data) return;

    const { role, speaker, user } = settings.data.data;
    form.setFieldsValue({
      role_events: role.role_events,
      preview_forward_weeks: role.preview_forward_weeks,
      apply_km_range: speaker.apply_km_range,
      max_km_range: speaker.max_km_range,
      min_weeks_without_assignations: speaker.min_weeks_without_assignations,
      min_weeks_in_advance: speaker.min_weeks_in_advance,
      rewind_weeks: speaker.rewind_weeks,
      forward_weeks: speaker.forward_weeks,
      accept_lunch_hospitality: speaker.accept_lunch_hospitality,
      accept_dinner_hospitality: speaker.accept_dinner_hospitality,
      contact_me: speaker.contact_me,
      reminder_mail: speaker.reminder_mail,
      reminder_mail_days_before: speaker.reminder_mail_days_before,
      phone: user.phone ?? "",
      mobile: user.mobile ?? "",
      private_email: user.private_email ?? "",
      zip_code: user.zip_code ?? "",
      country: user.country ?? "",
    });
  }, [settings.data, form]);

  const update = useMutation({
    mutationFn: (values: SpeakerSettingsValues) =>
      apiFetch<NotificationsResponse>("/speaker/settings", { method: "PUT", body: values }),
    onSuccess: (data) => {
      notify(data.notifications);
      // The answer carries the geocoding notice when the address changed while
      // the range limit is on, so the reloaded ranges are worth having too.
      void queryClient.invalidateQueries({ queryKey: ["speaker", "settings"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const ranges = settings.data?.data.ranges;

  const weeksWithoutAssignations = useMemo(
    () => optionsOf(ranges?.min_weeks_without_assignations),
    [ranges],
  );
  const weeksInAdvance = useMemo(() => optionsOf(ranges?.min_weeks_in_advance), [ranges]);
  const rewindWeeks = useMemo(() => optionsOf(ranges?.rewind_weeks), [ranges]);
  const forwardWeeks = useMemo(() => optionsOf(ranges?.forward_weeks), [ranges]);
  const previewWeeks = useMemo(() => optionsOf(ranges?.preview_forward_weeks), [ranges]);
  const reminderDays = useMemo(() => optionsOf(ranges?.reminder_mail_days_before), [ranges]);

  if (settings.isPending) return <Spin />;

  if (settings.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={
          settings.error instanceof Error ? settings.error.message : String(settings.error)
        }
      />
    );
  }

  const speaker = settings.data?.data.speaker;
  const observer = settings.data?.data.role.observer ?? false;
  const hint = (text: string) => <Typography.Text type="secondary">{text}</Typography.Text>;

  return (
    <Card title={t("navigation.speaker_settings")} style={{ maxWidth: 720 }}>
      <Form<SpeakerSettingsValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => update.mutate(values)}
      >
        <Divider titlePlacement="start">
          {t("role.headers.address")}
        </Divider>

        <Form.Item
          name="zip_code"
          label={t("activerecord.attributes.user.zip_code")}
          extra={hint(t("user.hints.zip_code"))}
        >
          <Input style={{ width: 140 }} />
        </Form.Item>

        <Form.Item name="country" label={t("activerecord.attributes.user.country")}>
          <CountrySelect style={{ width: 240 }} />
        </Form.Item>

        <Form.Item name="phone" label={t("activerecord.attributes.user.phone")}>
          <Input />
        </Form.Item>

        <Form.Item name="mobile" label={t("activerecord.attributes.user.mobile")}>
          <Input />
        </Form.Item>

        <Form.Item
          name="private_email"
          label={t("activerecord.attributes.user.private_email")}
          rules={[{ type: "email" }]}
        >
          <Input />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("role.headers.settings")}
        </Divider>

        {/* The three flags the speaker cannot set: access is granted on the role
            management screen, the other two by the local coordinator on
            /speaker/manage. Shown because they change what the rest means. */}
        <Form.Item>
          <Checkbox checked={observer} disabled>
            {t("activerecord.attributes.role.observer")}
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Checkbox checked={speaker?.simultaneous_absence_limit ?? false} disabled>
            {t("activerecord.attributes.speaker.simultaneous_absence_limit")}
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Checkbox checked={speaker?.external ?? false} disabled>
            {t("activerecord.attributes.speaker.external")}
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="max_km_range"
          label={t("activerecord.attributes.speaker.max_km_range")}
          extra={hint(t("speaker.hints.max_km_range"))}
        >
          <InputNumber min={0} style={{ width: 140 }} />
        </Form.Item>

        <Form.Item
          name="apply_km_range"
          valuePropName="checked"
          extra={hint(t("speaker.hints.apply_km_range"))}
        >
          <Checkbox>{t("activerecord.attributes.speaker.apply_km_range")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="role_events"
          valuePropName="checked"
          extra={hint(t("role.hints.role_events"))}
        >
          <Checkbox>{t("activerecord.attributes.role.role_events")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="min_weeks_without_assignations"
          label={t("activerecord.attributes.speaker.min_weeks_without_assignations")}
          extra={hint(t("speaker.hints.min_weeks_without_assignations"))}
        >
          <Select options={weeksWithoutAssignations} style={{ width: 120 }} />
        </Form.Item>

        <Form.Item
          name="min_weeks_in_advance"
          label={t("activerecord.attributes.speaker.min_weeks_in_advance")}
          extra={hint(t("speaker.hints.min_weeks_in_advance"))}
        >
          <Select options={weeksInAdvance} style={{ width: 120 }} />
        </Form.Item>

        <Form.Item
          name="preview_forward_weeks"
          label={t("activerecord.attributes.role.preview_forward_weeks")}
          extra={hint(t("role.hints.preview_forward_weeks"))}
        >
          <Select options={previewWeeks} style={{ width: 120 }} />
        </Form.Item>

        <Form.Item
          name="rewind_weeks"
          label={t("activerecord.attributes.speaker.rewind_weeks")}
          extra={hint(t("speaker.hints.rewind_weeks"))}
        >
          <Select options={rewindWeeks} style={{ width: 120 }} />
        </Form.Item>

        <Form.Item
          name="forward_weeks"
          label={t("activerecord.attributes.speaker.forward_weeks")}
          extra={hint(t("speaker.hints.forward_weeks"))}
        >
          <Select options={forwardWeeks} style={{ width: 120 }} />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("speaker.headers.course_of_hospitality")}
        </Divider>

        <Form.Item
          name="accept_lunch_hospitality"
          valuePropName="checked"
          extra={hint(t("speaker.hints.accept_lunch_hospitality"))}
        >
          <Checkbox>{t("activerecord.attributes.speaker.accept_lunch_hospitality")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="accept_dinner_hospitality"
          valuePropName="checked"
          extra={hint(t("speaker.hints.accept_dinner_hospitality"))}
        >
          <Checkbox>{t("activerecord.attributes.speaker.accept_dinner_hospitality")}</Checkbox>
        </Form.Item>

        <Divider titlePlacement="start">
          {t("speaker.headers.assignation_reminder")}
        </Divider>

        <Form.Item
          name="contact_me"
          valuePropName="checked"
          extra={hint(t("speaker.hints.contact_me"))}
        >
          <Checkbox>{t("activerecord.attributes.speaker.contact_me")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="reminder_mail"
          valuePropName="checked"
          extra={hint(t("speaker.hints.reminder_mail"))}
        >
          <Checkbox>{t("activerecord.attributes.speaker.reminder_mail")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="reminder_mail_days_before"
          label={t("activerecord.attributes.speaker.reminder_mail_days_before")}
          extra={hint(t("speaker.hints.reminder_mail_days_before"))}
        >
          {/* The old view offered a blank option here: no second reminder. */}
          <Select options={reminderDays} style={{ width: 120 }} allowClear />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={update.isPending}>
          {t("form.update")}
        </Button>
      </Form>
    </Card>
  );
}
